// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The last thing between a thrown render and a white page (M3-43).
//
// **`createRoot(host).render(…)` had nothing above it.** React's contract is that an error thrown
// during render unmounts the entire tree — not the failing component, the whole application — and
// with no boundary the result is `#root` emptied to a blank body. On the front door. To a stranger
// who has been there for two seconds. With no message, no reload prompt, and nothing in the page
// to say whether it is the site or their network.
//
// The 3-D view already had its own answer: `onRenderFailure` catches a WebGL context that could
// not be built and the stage says so in place of the machine. That covers the *expected* failure
// and nothing else — a bad read of a stored replay, a route that throws on a malformed URL, a
// component that meets a shape the contract said was impossible. Those are the ones that need a
// boundary, precisely because nobody predicted them.
//
// WHAT IT SAYS, AND WHAT IT DOES NOT SAY
// --------------------------------------
// **No stack trace and no error message on screen.** 03 §5's rule for the wire is that nothing
// about a failure crosses it, and the same reasoning holds for a page: a message written for a
// developer tells a player nothing they can act on, and it can carry a file path or a fragment of
// somebody's data. The console gets the real thing, because that is where somebody debugging will
// look.
//
// **One action, and it is the one that works.** Reloading is what fixes a corrupted module graph,
// a half-applied update and a transient chunk fetch, which is most of what reaches here. A "try
// again" that re-rendered the same broken tree would fail identically and teach the player that
// the button does nothing.
//
// **The offer to keep playing is real.** A failure in the account surface, the boards or the
// builder does not stop NEON NOVA working, and this product's whole shape is that the machine
// needs no backend and no account. So the second action goes to the front door rather than
// leaving somebody on a dead end.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { readLanguage, translatorFor } from '../i18n'

interface ErrorBoundaryProps {
  readonly children: ReactNode
  /** Told what happened, so a future diagnostics batch can carry it. Never shown to a player. */
  readonly onError?: (error: unknown, componentStack: string) => void
}

interface ErrorBoundaryState {
  readonly failed: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { failed: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console, not the page. `error.unhandled` is already in the diagnostics vocabulary
    // (`server/ts/diagnostics.ts`), so when the client starts sending batches this is where the
    // event is raised from — it is deliberately not sent yet, because nothing else is either.
    console.error('[pinball] a render failed', error, info.componentStack)
    this.props.onError?.(error, info.componentStack ?? '')
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children
    // **Read straight from storage, not through the hook.** This is a class component, so it could
    // not use one anyway — but the deeper reason is that this is the screen shown when the tree
    // below it has already thrown, and a boundary that depended on a live subscription to render
    // its own apology is a boundary that can fail to appear. `translatorFor` is pure and
    // `readLanguage` never throws.
    const t = translatorFor(readLanguage())
    return (
      <section className="page">
        <h1>{t('boundary.heading')}</h1>
        <p className="muted">{t('boundary.body')}</p>
        <div className="choices" role="group" aria-label={t('boundary.what-next')}>
          <button
            type="button"
            className="button button--primary"
            title={t('boundary.reload-title')}
            onClick={() => {
              globalThis.location?.reload()
            }}
          >
            {t('boundary.reload')}
          </button>
          {/*
            A full navigation rather than a router link. The router lives inside the tree that just
            threw, so asking it to move is asking the broken thing to fix itself; `assign` rebuilds
            the application from scratch, which is the point.
          */}
          <button
            type="button"
            className="button"
            title={t('boundary.back-title')}
            onClick={() => {
              globalThis.location?.assign('/')
            }}
          >
            {t('boundary.back')}
          </button>
        </div>
      </section>
    )
  }
}
